import { RUN_ID_NATIVE_PREFIX } from './presentation';
import type { Edge, LineageLens } from '../api/lineage';

/* Which provider an assertion came from. Pure functions, tested without a DOM.
 *
 * **`EdgeSource` cannot answer this yet, and that is why the run id is read instead.** The enum has
 * four members — `openlineage_facet`, `sql_parse`, `llm_inference`, `manual` — and a lineage harvest
 * that reads Snowflake's or Unity Catalog's own lineage catalogue stores its edges as
 * `openlineage_facet`. That is the right *standing* (the engine that ran the statement declared it,
 * so it is fact rather than inference) and the wrong *provenance*: it does not distinguish an edge
 * we went and fetched from one a producer pushed to `/v1/lineage/events`.
 *
 * `native_harvest.RUN_ID_PREFIX` exists for exactly this reason and says so: the run id is
 * `native-<connection>-<digest>`, *"on the record for ever, because `Edge.run_id` is stored: an
 * auditor asking 'why does the graph think this' can tell an edge this platform read out of a
 * warehouse's own catalogue from one a pipeline pushed"*. So the distinction is real and recorded;
 * it is simply recorded in the run id rather than in the enum.
 *
 * **This is a presentation reading of a stored field, not a new source of truth**, and it is
 * deliberately conservative: anything that does not carry the prefix is reported as pushed rather
 * than guessed at. A `warehouse_native` member on `EdgeSource` is the proper fix and is a migration,
 * a contract change, a generated client and a copy key — recorded rather than smuggled in here. When
 * it lands, this function becomes a one-line read of `edge.source` and its tests keep meaning what
 * they mean.
 */

export interface Assertion {
  readonly edge: Edge;
  /** Whether this platform fetched the assertion from the source's own lineage catalogue. */
  readonly native: boolean;
}

/** Whether an edge was harvested from a source's own lineage catalogue. */
export function isNative(edge: Edge): boolean {
  return (edge.run_id ?? '').startsWith(RUN_ID_NATIVE_PREFIX);
}

/**
 * One node's assertions, both directions, deduplicated and ordered for reading.
 *
 * **Deduplicated on the edge id, because the two reads overlap.** A self-referential flow would come
 * back from both the upstream and the downstream query, and a table listing it twice reads as two
 * assertions where there is one. The domain refuses a self-loop, so this is defence rather than a
 * live case — and it is cheap next to the alternative of a reader counting an edge twice.
 *
 * **Ordered native-first, then by when it was observed, newest first.** Native assertions are the
 * ones a reader is here to check, and among assertions the recent one is the one that explains
 * today's graph. Both keys are stable, so the table does not reshuffle between renders.
 */
export function assertionsOf(
  upstream: readonly Edge[],
  downstream: readonly Edge[],
): readonly Assertion[] {
  const seen = new Map<string, Edge>();
  for (const edge of [...upstream, ...downstream]) {
    if (!seen.has(edge.id)) seen.set(edge.id, edge);
  }
  return [...seen.values()]
    .map((edge) => ({ edge, native: isNative(edge) }))
    .sort((left, right) => {
      if (left.native !== right.native) return left.native ? -1 : 1;
      return right.edge.valid_from.localeCompare(left.edge.valid_from);
    });
}

/** What a caller renders under an assertion's provider badge: an i18n key and its values.
 *
 * **A key rather than a rendered string**, which is this model layer's own convention —
 * `completenessKey` and `summaryKey` do the same. It keeps the decision testable without a DOM or a
 * translator, and it is what stops a model function needing i18next's `t` in its signature.
 *
 * **Three cases and no fourth**, because each is a different answer to "who says so". A native
 * harvest names the connection it read through; a pushed run event names the producer's own run id;
 * a manual assertion names the person, which `Edge.asserted_by` is *always* present for — an
 * unattributable human assertion being indistinguishable from a fabricated one.
 *
 * The connection id is truncated to eight characters. It is there so an operator can tell two
 * harvests apart, not so anybody reads a UUID — the connection's *name* is already on every node in
 * the graph above.
 */
export function runLabelFor(edge: Edge): {
  readonly key: string;
  readonly values: Record<string, string>;
} {
  if (edge.asserted_by != null && edge.asserted_by !== '') {
    return { key: 'lineage.native.assertedBy', values: { who: edge.asserted_by } };
  }
  const run = edge.run_id ?? '';
  if (run === '') {
    /* No run id at all. Said rather than left blank: an assertion whose origin is unrecorded is a
       fact about our own record-keeping, and a blank cell reads as a rendering fault. */
    return { key: 'lineage.native.noRun', values: {} };
  }
  if (isNative(edge)) {
    /* `native-<uuid>-<digest>`: a uuid is five dash-separated groups, so the connection is groups
       1..5 and the digest is the last. Rebuilt from the parts rather than sliced by character count,
       so a change to the prefix cannot silently shift which characters are shown. */
    const connection = run.split('-').slice(1, 6).join('-');
    return {
      key: 'lineage.native.harvested',
      values: { connection: connection.slice(0, 8) },
    };
  }
  return {
    key: 'lineage.native.pushedRun',
    values: { run: run.length > 24 ? `${run.slice(0, 23)}…` : run },
  };
}

/** Which of a node's assertions belong under one lens.
 *
 * `useNodeEdges` is the inspection path and stays deliberately unfiltered — a `needs_review` edge
 * has to be visible somewhere or it can never be reviewed, and filtering the fetch itself would
 * make the review queue unreachable from the thing being reviewed. So the lens narrows the table
 * built from that unfiltered read, using the same `isNative()` test the graph's own `lens` query
 * parameter applies server-side (both read the identical `RUN_ID_NATIVE_PREFIX`), so the two can
 * never disagree about which bucket a flow is in.
 */
export function filterByLens(
  assertions: readonly Assertion[],
  lens: LineageLens,
): readonly Assertion[] {
  if (lens === 'combined') return assertions;
  const wantNative = lens === 'native';
  return assertions.filter((assertion) => assertion.native === wantNative);
}
